import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Input, Avatar, Chip, Spinner, Button } from '@heroui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faClock,
  faUser,
  faFileText,
  faBookOpen,
  faFile,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { searchAll, SearchResult } from 'src/api/search';
import { debounce } from 'lodash';
import DocumentsApi from 'src/api/documents';

interface GlobalSearchProps {
  variant?: 'input' | 'button';
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ variant = 'input' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const navigate = useNavigate();

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchQuery: string) => {
        if (!searchQuery.trim()) {
          setResults([]);
          setIsLoading(false);
          return;
        }

        try {
          setIsLoading(true);
          const response = await searchAll({
            query: searchQuery,
            limit: 8
          });
          setResults(response.results);
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, 300),
    []
  );

  // Trigger search when mobile modal opens
  React.useEffect(() => {
    if (isMobileModalOpen && query.trim()) {
      debouncedSearch(query);
    }
  }, [isMobileModalOpen, query, debouncedSearch]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setIsOpen(!!value.trim() && variant === 'input');

    if (value.trim()) {
      setIsLoading(true);
      debouncedSearch(value);
    } else {
      setResults([]);
      setIsLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false);
    setIsMobileModalOpen(false);
    setQuery('');

    // Navigate based on result type and available data
    if (result.type === 'campaigns' && result.dbInfo?.campaign) {
      navigate(`/campaigns/${result.dbInfo.campaign.id}`); // Navigate to campaigns list for now
    } else if (result.type === 'sessions' && result.metadata.sessionId) {
      navigate(`/sessions/${result.metadata.sessionId}/transcript`);
    } else if (
      (result.type === 'transcripts' || result.type === 'summaries' || result.type === 'recaps') &&
      result.metadata.sessionId
    ) {
      // Navigate to session transcript with tab parameter
      let tabParam = '?tab=transcript';
      if (result.type === 'summaries') tabParam = '?tab=summary';
      if (result.type === 'recaps') tabParam = '?tab=notes';
      navigate(`/sessions/${result.metadata.sessionId}/transcript${tabParam}`);
    } else if (result.type === 'documents' && result.metadata.documentId) {
      // Get the signed S3 URL from backend and open document
      handleDocumentClick(result.metadata.documentId);
    }
  };

  const handleDocumentClick = async (documentId: string) => {
    try {
      const response = await DocumentsApi.getDocumentViewUrl(documentId);

      const { viewUrl } = response;
      if (!viewUrl) {
        console.error('No viewUrl in response:', response);
        return;
      }

      // Open the document in a new tab
      window.open(viewUrl, '_blank');
    } catch (error) {
      console.error('Failed to get document view URL:', error);
      // You could show a toast notification here if you have a toast system
    }
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'campaigns':
        return faBookOpen;
      case 'sessions':
        return faClock;
      case 'transcripts':
        return faUser;
      case 'summaries':
        return faFileText;
      case 'recaps':
        return faFileText;
      case 'documents':
        return faFile;
      default:
        return faFileText;
    }
  };

  const getResultTypeColor = (type: string) => {
    switch (type) {
      case 'campaigns':
        return 'primary';
      case 'sessions':
        return 'secondary';
      case 'transcripts':
        return 'success';
      case 'summaries':
        return 'warning';
      case 'recaps':
        return 'danger';
      case 'documents':
        return 'default';
      default:
        return 'default';
    }
  };

  const getDisplayType = (type: string) => {
    switch (type) {
      case 'campaigns':
        return 'campaign';
      case 'sessions':
        return 'session';
      case 'transcripts':
        return 'transcript';
      case 'summaries':
        return 'summary';
      case 'recaps':
        return 'highlight';
      case 'players':
        return 'player';
      case 'documents':
        return 'document';
      default:
        return type;
    }
  };

  const formatResultContent = (result: SearchResult) => {
    const maxLength = 180;
    return result.content.length > maxLength
      ? result.content.substring(0, maxLength) + '...'
      : result.content;
  };

  const formatResultTitle = (result: SearchResult) => {
    if (result.dbInfo?.campaign?.title && result.dbInfo?.session?.title) {
      return `${result.dbInfo.campaign.title} • ${result.dbInfo.session.title}`;
    }
    if (result.dbInfo?.campaign?.title) {
      return result.dbInfo.campaign.title;
    }
    if (result.metadata.campaignTitle && result.metadata.sessionTitle) {
      return `${result.metadata.campaignTitle} • ${result.metadata.sessionTitle}`;
    }
    return result.metadata.title ?? result.metadata.campaignTitle ?? 'Search Result';
  };

  const formatResultSubtitle = (result: SearchResult) => {
    if (result.type === 'transcripts' && result.metadata.speaker) {
      return `${result.metadata.speaker}`;
    }
    if (result.type === 'summaries') {
      return 'Session Summary';
    }
    if (result.type === 'recaps') {
      return 'Session Highlight';
    }
    return result.type.charAt(0).toUpperCase() + result.type.slice(1);
  };

  // Sort results by confidence score (highest first)
  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => b.score - a.score);
  }, [results]);

  const SearchResults = () => (
    <>
      {sortedResults.length === 0 ? (
        <div className="p-4 text-center text-gray-500">
          {query.trim()
            ? isLoading
              ? 'Searching...'
              : 'No results found'
            : 'Start typing to search...'}
        </div>
      ) : (
        sortedResults.map((result) => (
          <div
            key={`${result.type}-${result.id}`}
            onClick={() => handleResultClick(result)}
            className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer flex items-start gap-4 border-b border-gray-50 dark:border-gray-800 last:border-b-0">
            <Avatar
              icon={<FontAwesomeIcon icon={getResultIcon(result.type)} />}
              size="sm"
              className="bg-gray-100 dark:bg-gray-700 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                    {formatResultTitle(result)}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {formatResultSubtitle(result)}
                  </div>
                  <div className="text-xs text-gray-500 leading-relaxed mt-1 line-clamp-2">
                    {formatResultContent(result)}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Chip
                    size="sm"
                    variant="flat"
                    color={
                      getResultTypeColor(result.type) as
                        | 'primary'
                        | 'secondary'
                        | 'success'
                        | 'warning'
                        | 'danger'
                        | 'default'
                    }>
                    {getDisplayType(result.type)}
                  </Chip>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </>
  );

  // Mobile button variant
  if (variant === 'button') {
    return (
      <>
        <Button
          variant="light"
          size="sm"
          onPress={() => setIsMobileModalOpen(true)}
          startContent={<FontAwesomeIcon icon={faSearch} />}
          className="w-full sm:w-auto">
          Search
        </Button>

        {isMobileModalOpen &&
          createPortal(
            <div
              className="fixed inset-0 z-50 bg-white dark:bg-gray-900"
              role="dialog"
              aria-modal="true"
              aria-label="Search">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center gap-3 p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <Input
                    placeholder="Search campaigns, sessions, transcripts..."
                    value={query}
                    onValueChange={handleInputChange}
                    startContent={<FontAwesomeIcon icon={faSearch} className="text-gray-400" />}
                    endContent={isLoading && <Spinner size="sm" />}
                    variant="bordered"
                    size="lg"
                    className="flex-1"
                    autoFocus
                    aria-label="Search input"
                  />
                  <Button
                    variant="light"
                    isIconOnly
                    onPress={() => setIsMobileModalOpen(false)}
                    size="sm"
                    aria-label="Close search">
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900 p-4">
                  {results.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      {query.trim()
                        ? isLoading
                          ? 'Searching...'
                          : 'No results found'
                        : 'Start typing to search...'}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {results.map((result) => (
                        <div
                          key={`${result.type}-${result.id}`}
                          onClick={() => {
                            handleResultClick(result);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleResultClick(result);
                          }}
                          className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 transition-colors touch-manipulation"
                          role="button"
                          tabIndex={0}>
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                              <FontAwesomeIcon
                                icon={getResultIcon(result.type)}
                                className="text-default-500"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                                {formatResultTitle(result)}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {formatResultSubtitle(result)}
                              </div>
                              <div className="text-xs text-gray-500 leading-relaxed mt-2 line-clamp-2">
                                {formatResultContent(result)}
                              </div>
                              <div className="mt-2">
                                <Chip
                                  size="sm"
                                  variant="flat"
                                  color={
                                    getResultTypeColor(result.type) as
                                      | 'primary'
                                      | 'secondary'
                                      | 'success'
                                      | 'warning'
                                      | 'danger'
                                      | 'default'
                                  }>
                                  {getDisplayType(result.type)}
                                </Chip>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
      </>
    );
  }

  // Desktop input variant
  return (
    <div className="relative w-full">
      <Input
        placeholder="Search campaigns, sessions, transcripts..."
        value={query}
        onValueChange={handleInputChange}
        startContent={<FontAwesomeIcon icon={faSearch} className="text-gray-400" />}
        endContent={isLoading && <Spinner size="sm" />}
        variant="bordered"
        size="sm"
        className="w-full"
        onFocus={() => query.trim() && setIsOpen(true)}
        onBlur={() => {
          // Delay closing to allow click events on results
          setTimeout(() => setIsOpen(false), 200);
        }}
      />

      {isOpen && (
        <div className="absolute top-full left-0 right-0 lg:right-auto lg:w-[32rem] z-50 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          <SearchResults />
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
